import { useCallback } from 'react';

import { CreateCommentInput } from '@this+that/models';
import log from 'loglevel';

import { useInboxEntryCommentsSlice } from './useInboxEntryCommentsSlice';
import {
  MutationResult,
  useCreateComment,
} from '../../services/inboxEntryComments.service';

export const useCreateInboxEntryComment = (inboxEntryId: string) => {
  const { createComment: createCommentService } = useCreateComment();

  const { creatingComment, setCreatingComment, addComment, setError } =
    useInboxEntryCommentsSlice(inboxEntryId);

  const createComment = useCallback(
    async (
      input: Omit<CreateCommentInput, 'inboxEntryId'>,
    ): Promise<MutationResult<null>> => {
      setCreatingComment(true);
      setError(null);

      try {
        const { data, errors } = await createCommentService({
          ...input,
          inboxEntryId,
        });

        if (errors?.length) {
          log.error(
            `Error creating comment for inbox entry ${inboxEntryId}:`,
            errors,
          );
          return { data: null, errors };
        }

        if (data) {
          addComment(data);
        }

        return { data: null };
      } catch (error) {
        log.error(
          `Error creating comment for inbox entry ${inboxEntryId}:`,
          error,
        );
        const message =
          error instanceof Error ? error.message : 'Unknown error';
        return { data: null, errors: [{ message }] };
      } finally {
        setCreatingComment(false);
      }
    },
    [
      inboxEntryId,
      createCommentService,
      addComment,
      setCreatingComment,
      setError,
    ],
  );

  return { createComment, creatingComment };
};
